import { FC, useEffect, useRef, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import {
  giftCouponThemeDataAtomFamily,
  giftCouponThemeListDataAtom,
  giftCouponThemeStatusAtomFamily,
} from '@shared/api/giftCouponTheme/giftCouponTheme.atom';
import {
  createGiftCouponTheme,
  updateGiftCouponTheme,
} from '@shared/api/giftCouponTheme/giftCouponTheme.controller';
import { useGiftCouponThemeHook } from '@shared/api/giftCouponTheme/giftCouponTheme.hook';
import { postImage } from '@shared/api/uploadImage';
import { Button } from '@shared/components/Button';
import { Loading } from '@shared/components/Loading';
import { TextField } from '@shared/components/TextField';
import { useAuth } from '@shared/state/hooks/useAuth';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { useNavigate, useParams } from 'react-router-dom';

export const GiftCouponThemeFormPage: FC = function GiftCouponThemeFormPage() {
  const { themeId } = useParams<{ themeId: string }>();
  const navigate = useNavigate();
  const { sellerRole } = useAuth();

  const isEditMode = Boolean(themeId);

  // 수정 모드일 때 데이터 조회
  useGiftCouponThemeHook(themeId ?? '', sellerRole(), !isEditMode);
  const status = useRecoilValue(giftCouponThemeStatusAtomFamily(themeId ?? ''));
  const themeData = useRecoilValue(giftCouponThemeDataAtomFamily(themeId ?? ''));

  // 목록 데이터 업데이트용
  const setListData = useSetRecoilState(giftCouponThemeListDataAtom);

  // 폼 상태
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [order, setOrder] = useState(0);
  const [isEnabled, setIsEnabled] = useState(true);
  const [imageId, setImageId] = useState<string>('');
  const [imagePreview, setImagePreview] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 수정 모드일 때 기존 데이터 로드 — 최초 1회만. 창 포커스 refetch로 atom이
  // 새 객체가 되어도 입력 중인 값을 덮어쓰지 않는다.
  const didInitRef = useRef(false);
  useEffect(() => {
    if (isEditMode && themeData && !didInitRef.current) {
      didInitRef.current = true;
      setName(themeData.name);
      setCode(themeData.code);
      setOrder(themeData.order);
      setIsEnabled(themeData.isEnabled);
      if (themeData.image?._id) {
        setImageId(themeData.image._id);
      }
      if (themeData.image?.url) {
        setImagePreview(fileBucketUrl(themeData.image.url, 'w400'));
      }
    }
  }, [isEditMode, themeData]);

  // 이미지 업로드 핸들러
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      setImagePreview(URL.createObjectURL(file));

      const uploadedImage = await postImage(file, sellerRole());
      setImageId(uploadedImage._id);
    } catch (error) {
      console.error(error);
      alert('이미지 업로드에 실패했습니다.');
      setImagePreview('');
    } finally {
      setIsUploading(false);
    }
  };

  // 제출 핸들러
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      alert('테마명을 입력해주세요.');
      return;
    }
    if (!code.trim()) {
      alert('테마 코드를 입력해주세요.');
      return;
    }
    if (!imageId) {
      alert('테마 이미지를 업로드해주세요.');
      return;
    }

    try {
      setIsSubmitting(true);

      const payload = {
        name,
        code,
        order,
        isEnabled,
        image: imageId,
      };

      if (isEditMode && themeId) {
        // 수정
        const updated = await updateGiftCouponTheme(themeId, payload, sellerRole());
        setListData(
          (prev) => prev?.map((item) => (item._id === themeId ? updated : item)) ?? null,
        );
        alert('수정되었습니다.');
      } else {
        // 생성
        const created = await createGiftCouponTheme(payload, sellerRole());
        setListData((prev) => (prev ? [...prev, created] : [created]));
        alert('등록되었습니다.');
      }

      navigate('/gift-seller/coupon-theme');
    } catch (error) {
      console.error(error);
      alert(isEditMode ? '수정에 실패했습니다.' : '등록에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 로딩 중 (수정 모드)
  if (isEditMode && (status === 'loading' || status === 'idle')) {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-gray-100"
          >
            ←
          </button>
          <h1 className="text-xl font-bold">
            교환권 테마 {isEditMode ? '수정' : '등록'}
          </h1>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="p-4">
        <div className="space-y-4">
          {/* 이미지 업로드 */}
          <div className="rounded-lg bg-white p-4 shadow-sm">
            <label className="mb-2 block text-sm font-medium text-gray-700">
              테마 이미지 <span className="text-red-500">*</span>
            </label>
            <p className="mb-3 text-xs text-gray-500">
              교환권 배경으로 사용될 이미지입니다. 3:4 비율 권장
            </p>

            <div className="flex gap-4">
              {/* 미리보기 */}
              <div className="relative aspect-[3/4] w-32 overflow-hidden rounded-lg border bg-gray-100">
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="미리보기"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-xs text-gray-400">
                    이미지 없음
                  </div>
                )}
                {isUploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                    <Loading />
                  </div>
                )}
              </div>

              {/* 업로드 버튼 */}
              <div className="flex flex-col justify-center">
                <label className="cursor-pointer rounded-lg border border-indigo-300 bg-indigo-50 px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-100">
                  {isUploading ? '업로드 중...' : '이미지 선택'}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={isUploading}
                    className="hidden"
                  />
                </label>
                {imagePreview && (
                  <button
                    type="button"
                    onClick={() => {
                      setImageId('');
                      setImagePreview('');
                    }}
                    className="mt-2 text-xs text-red-500 hover:underline"
                  >
                    이미지 제거
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* 기본 정보 */}
          <div className="rounded-lg bg-white p-4 shadow-sm">
            <h3 className="mb-4 font-medium text-gray-900">기본 정보</h3>
            <div className="space-y-4">
              <TextField
                label="테마명"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="예: 기독교, 불교, 일반"
                required
              />

              <TextField
                label="테마 코드"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="예: christian, buddhist, default"
                required
              />
              <p className="text-xs text-gray-500">
                영문 소문자로 입력해주세요. 시스템에서 식별용으로 사용됩니다.
              </p>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  정렬 순서
                </label>
                <input
                  type="number"
                  value={order}
                  onChange={(e) => setOrder(Number(e.target.value))}
                  className="w-full rounded-lg border px-3 py-2"
                  min={0}
                />
                <p className="mt-1 text-xs text-gray-500">
                  숫자가 작을수록 먼저 표시됩니다.
                </p>
              </div>

              {/* 활성화 토글 */}
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <p className="font-medium text-gray-900">활성화</p>
                  <p className="text-xs text-gray-500">
                    비활성화하면 구매자에게 표시되지 않습니다.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsEnabled(!isEnabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                    isEnabled ? 'bg-indigo-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                      isEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 버튼 */}
        <div className="mt-4 flex gap-3">
          <Button
            type="button"
            onClick={() => navigate(-1)}
            className="flex-1 rounded-lg border border-gray-300 bg-white py-3 font-medium text-gray-700"
          >
            취소
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting || isUploading}
            className="flex-1 rounded-lg bg-indigo-600 py-3 font-medium text-white disabled:opacity-50"
          >
            {isSubmitting ? '저장 중...' : isEditMode ? '수정하기' : '등록하기'}
          </Button>
        </div>
      </form>
    </div>
  );
};
